// ════════════════════════════════════════════════════════════════════════
//  Rules around the set of versions — no React, no queries
//
//  These four functions are the whole reason the editorial app knows what a
//  draft is. They used to sit inside the provider, where the only way to check
//  them was to render a page and read the screen.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle } from "@otto/lehrgang";
import type { Version } from "../../features/versions/lib/data.js";

export type VersionSplit = {
  /** The open draft, if there is one. At most one exists at a time. */
  readonly draft: Version | null;
  /** Published versions, newest first. */
  readonly published: readonly Version[];
  readonly latestPublished: Version | null;
};

/**
 * Splits the version list into "the draft" and "what is published".
 *
 * Expects the list newest first, the way the query returns it — the newest
 * published version is what a new class gets pinned to.
 */
export function splitVersions(all: readonly Version[]): VersionSplit {
  const draft = all.find((v) => v.publishedAt === null) ?? null;
  const published = all.filter((v) => v.publishedAt !== null);
  return { draft, published, latestPublished: published[0] ?? null };
}

/**
 * The next free version number.
 *
 * Derived from the maximum, not from the count: a discarded draft leaves a hole,
 * and reusing its number would give two different contents the same name.
 */
export function nextVersionNumber(all: readonly Version[]): number {
  return Math.max(0, ...all.map((v) => v.version)) + 1;
}

/**
 * All stage keys of a published bundle.
 *
 * Published keys are fixed: children's progress refers to them, so renaming one
 * would silently move a child's stars to another stage. The editor uses this set
 * to lock those keys.
 */
export function stageKeysOf(bundle: Bundle | null): ReadonlySet<string> {
  return new Set(bundle?.lessons.flatMap((l) => l.stages.map((s) => s.key)) ?? []);
}
